<template>
  <div class="api-log-container">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h1 class="page-title">API Request Logs</h1>
          <p class="page-subtitle">Monitor and debug API requests across all tenants</p>
        </div>
        <div class="header-right">
          <button class="btn btn-refresh-header" @click="loadData" :disabled="page.isLoading">
            <i class="fas fa-sync-alt me-2" :class="{ 'fa-spin': page.isLoading }"></i>
            Refresh Logs
          </button>
        </div>
      </div>
    </div>

    <!-- Main Card Container -->
    <div class="management-card">
      <!-- Advanced Filter Bar -->
      <div class="filter-bar">
        <div class="filter-left">
          <!-- Tenant Filter -->
          <div class="filter-group">
            <div class="section-label-tabs">
              <span class="label-tab">Tenant Cluster</span>
            </div>
            <div class="select-wrapper">
              <select v-model="filters.tenant" class="form-select-ext" @change="applyFilters">
                <option value="">All Clusters</option>
                <option v-for="tenant in tenantOptions" :key="tenant" :value="tenant">
                  {{ tenant }}
                </option>
              </select>
              <i class="fas fa-chevron-down select-icon"></i>
            </div>
          </div>

          <!-- Method Filter -->
          <div class="filter-group">
            <div class="section-label-tabs">
              <span class="label-tab">HTTP Method</span>
            </div>
            <div class="select-wrapper">
              <select v-model="filters.method" class="form-select-ext" @change="applyFilters">
                <option value="">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
              <i class="fas fa-chevron-down select-icon"></i>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="filter-group">
            <div class="section-label-tabs">
              <span class="label-tab">Status Code</span>
            </div>
            <div class="select-wrapper">
              <select v-model="filters.response_status" class="form-select-ext" @change="applyFilters">
                <option value="">All Status</option>
                <option value="200">2xx Success</option>
                <option value="400">4xx Client Error</option>
                <option value="500">5xx Server Error</option>
              </select>
              <i class="fas fa-chevron-down select-icon"></i>
            </div>
          </div>
        </div>
        
        <div class="filter-right">
          <div class="stats-summary">
            <span class="stats-label">Total Logs:</span>
            <span class="stats-value">{{ meta.total_count || 0 }}</span>
          </div>
          <button class="btn-reset-primary" @click="clearFilters" title="Reset All Filters">
            <i class="fas fa-undo-alt"></i>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="card-content">
        <!-- Loading State -->
        <div v-if="page.isLoading && !page.output" class="loading-state">
          <div class="loader-box">
            <div class="spinner-border text-danger" role="status"></div>
            <p>Fetching API logs...</p>
          </div>
        </div>

        <!-- Table Content -->
        <div v-else-if="page.output" class="table-outer">
          <div class="table-responsive">
            <table class="modern-list-table">
              <thead>
                <tr>
                  <th width="60" class="col-no">No</th>
                  <th width="160">Timestamp</th>
                  <th width="140">Tenant</th>
                  <th width="120">Method</th>
                  <th width="100">Type</th>
                  <th width="350">Request URL</th>
                  <th width="100">Status</th>
                  <th class="text-end pe-4" width="100">Details</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="apiLogs.length === 0">
                  <tr>
                    <td colspan="8">
                      <div class="empty-list">
                        <div class="empty-art">
                          <i class="fas fa-terminal"></i>
                        </div>
                        <h3>No API Logs Found</h3>
                        <p>No activity recorded with the current filter criteria.</p>
                        <button class="btn-clear-empty mt-3" @click="clearFilters">
                          <i class="fas fa-eraser me-2"></i>
                          Clear All Filters
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>

                <template v-else v-for="(item, index) in apiLogs" :key="item.id || index">
                  <tr 
                    :class="{'expanded': expandedRows.includes(index), 'error-row': item.response_status >= 400}"
                    @click="toggleRow(index)"
                    class="clickable-row"
                  >
                    <td class="col-no" @click.stop>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                    <td>
                      <div class="timestamp-box">
                        <span class="date">{{ formatDate(item.created_at) }}</span>
                        <span class="time">{{ formatTime(item.created_at) }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="tenant-pill">
                        <i class="fas fa-cube me-1"></i>
                        {{ item.tenant || 'Central' }}
                      </span>
                    </td>
                    <td>
                      <span class="method-pill" :class="item.method?.toLowerCase()">
                        {{ item.method }}
                      </span>
                    </td>
                    <td>
                      <span class="type-pill" :class="item.type">
                        {{ item.type?.toUpperCase() }}
                      </span>
                    </td>
                    <td>
                      <div class="url-path-box">
                        <span class="url-text" :title="item.url">{{ truncateUrl(item.url) }}</span>
                        <small class="ip-address">{{ item.ip }}</small>
                      </div>
                    </td>
                    <td>
                      <span class="status-pill" :class="getStatusClass(item.response_status)">
                        <span class="status-dot"></span>
                        {{ item.response_status }}
                      </span>
                    </td>
                    <td class="text-end pe-4">
                      <button 
                        class="action-btn" 
                        :class="{ active: expandedRows.includes(index) }"
                        @click.stop="toggleRow(index)"
                        title="Toggle Details"
                      >
                        <i class="fas" :class="expandedRows.includes(index) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                    </td>
                  </tr>

                  <!-- Expanded Detail Row -->
                  <tr v-if="expandedRows.includes(index)" class="detail-row">
                    <td colspan="8" class="detail-container">
                      <div class="detail-content">
                        <div class="row g-4">
                          <!-- Request Detail -->
                          <div class="col-md-6">
                            <div class="state-card flex-column">
                              <div class="state-header request">
                                <i class="fas fa-arrow-up me-2"></i>
                                Request Details
                              </div>
                              <div class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="formatRequestObject(item)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :theme="'dark'"
                                />
                              </div>
                            </div>
                          </div>
                          <!-- Response Detail -->
                          <div class="col-md-6">
                            <div class="state-card flex-column">
                              <div class="state-header response">
                                <i class="fas fa-arrow-down me-2"></i>
                                Response Details
                              </div>
                              <div class="state-body custom-json-pretty">
                                <vue-json-pretty 
                                  :data="formatResponseObject(item)" 
                                  :deep="1" 
                                  :showDoubleQuotes="true"
                                  :theme="'dark'"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination Footer -->
      <div class="card-footer" v-if="page.output && apiLogs.length > 0">
        <o-pagination 
          :page="meta.page"  
          :total="meta.total" 
          @callback="navPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { OPagination } from '@/components/OmnihControl'
import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import VueJsonPretty from "vue-json-pretty"
import "vue-json-pretty/lib/styles.css"
import api from '@/helpers/api'

export default {
  name: 'ExtranetApiLog',
  
  components: {
    OPagination,
    VueJsonPretty
  },

  setup() {
    const isMounted = ref(true)
    const expandedRows = ref([])
    
    const page = reactive({
      isLoading: false,
      output: false
    })

    const apiLogs = ref([])
    const tenantOptions = ref([])

    const meta = reactive({
      page: 1,
      limit: 15,
      total: 1,
      total_count: 0,
      sortBy: 'created_at',
      sortDir: 'desc'
    })

    const filters = reactive({
      tenant: '',
      method: '',
      response_status: ''
    })

    const loadData = async () => {
      if (!isMounted.value) return
      
      page.isLoading = true
      expandedRows.value = []

      try {
        const params = {
          custom: {
            page: meta.page,
            per_page: meta.limit,
            sort_by: meta.sortBy,
            sort_dir: meta.sortDir
          }
        }

        if (filters.tenant) params.custom.tenant_id = filters.tenant
        if (filters.method) params.custom.method = filters.method
        if (filters.response_status) params.custom.status_code = parseInt(filters.response_status)

        Object.keys(params.custom).forEach(key => {
          if (params.custom[key] === null || params.custom[key] === '') delete params.custom[key]
        })

        const response = await api.request('get', '/api/extranet/api-logs', params)

        if (!isMounted.value) return

        if (response.data && response.data.status) {
          const data = response.data.data
          apiLogs.value = data.data_res || []
          
          if (data.meta) {
            meta.page = parseInt(data.meta.page) || 1
            meta.limit = parseInt(data.meta.limit) || 15
            meta.total = parseInt(data.meta.total) || 1
            meta.total_count = data.meta.total_count || 0

            if (data.meta.tenants) {
              tenantOptions.value = data.meta.tenants
            }
          }
        }
        page.output = true
      } catch (error) {
        if (!isMounted.value) return
        console.error('API Log Load Error:', error)
      } finally {
        if (isMounted.value) {
          page.isLoading = false
        }
      }
    }

    function navPage(pageNumber) {
      if (pageNumber === meta.page) return
      meta.page = pageNumber
      loadData()
    }

    function applyFilters() {
      meta.page = 1
      loadData()
    }

    function clearFilters() {
      filters.tenant = ''
      filters.method = ''
      filters.response_status = ''
      meta.page = 1
      loadData()
    }

    function toggleRow(index) {
      const pos = expandedRows.value.indexOf(index)
      if (pos > -1) {
        expandedRows.value.splice(pos, 1)
      } else {
        expandedRows.value.push(index)
      }
    }

    function formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
      })
    }

    function formatTime(dateString) {
      if (!dateString) return ''
      return new Date(dateString).toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      })
    }

    function truncateUrl(url) {
      if (!url) return '-'
      const cleanUrl = url.replace(/^(?:https?:\/\/)?(?:[^/]+\.)?([^/]+\.[^/]+)/, '')
      if (cleanUrl.length <= 45) return cleanUrl || url
      return cleanUrl.substring(0, 42) + '...'
    }

    function parseJsonSafely(json) {
      if (!json) return null
      try {
        if (typeof json === 'string') return JSON.parse(json)
        return json
      } catch (e) {
        return json
      }
    }

    function formatRequestObject(item) {
      return {
        url: item.url,
        ip: item.ip,
        headers: parseJsonSafely(item.request_headers),
        payload: parseJsonSafely(item.request_body)
      }
    }

    function formatResponseObject(item) {
      return {
        status: item.response_status,
        body: parseJsonSafely(item.response_body)
      }
    }

    function getStatusClass(statusCode) {
      if (statusCode >= 200 && statusCode < 300) return 'success'
      if (statusCode >= 400 && statusCode < 500) return 'warning'
      if (statusCode >= 500) return 'danger'
      return ''
    }

    onMounted(() => {
        loadData()
    })

    onBeforeUnmount(() => {
        isMounted.value = false
    })

    return {
      page,
      meta,
      filters,
      apiLogs,
      expandedRows,
      tenantOptions,
      loadData,
      navPage,
      applyFilters,
      clearFilters,
      toggleRow,
      formatDate,
      formatTime,
      truncateUrl,
      formatRequestObject,
      formatResponseObject,
      getStatusClass
    }
  }
}
</script>

<style scoped>
.api-log-container {
    max-width: 1400px;
    margin: 0 auto;
}

/* Page Header */
.page-header {
    margin-bottom: 32px;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 4px;
}

.page-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
}

/* Management Card */
.management-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
}

/* Filter Bar */
.filter-bar {
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    background: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    gap: 24px;
}

.filter-left {
    display: flex;
    align-items: flex-end;
    gap: 24px;
    flex: 1;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 180px;
}

.section-label-tabs {
    display: flex;
}

.label-tab {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
}

.select-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.form-select-ext {
    appearance: none;
    width: 100%;
    padding: 10px 16px;
    background: #f8fafc;
    border: 1px solid var(--extranet-accent);
    border-radius: var(--extranet-rounded);
    font-size: 13px;
    color: #1e293b;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    transition: all 0.2s;
}

.form-select-ext:focus {
    border-color: var(--extranet-primary);
    background: white;
}

.select-icon {
    position: absolute;
    right: 12px;
    font-size: 10px;
    color: #94a3b8;
    pointer-events: none;
}

/* Buttons */
.btn-reset-primary, .btn-refresh-header, .btn-clear-empty {
    height: 38px;
    border: none;
    background: var(--extranet-primary);
    border-radius: var(--extranet-rounded);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    font-weight: 600;
}

.btn-reset-primary { width: 38px; }
.btn-refresh-header { padding: 0 20px; font-size: 14px; }
.btn-clear-empty { padding: 0 24px; margin: 0 auto; }

.btn-reset-primary:hover, .btn-refresh-header:hover, .btn-clear-empty:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
}

.filter-right {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: var(--extranet-rounded);
    font-size: 13px;
    border: 1px solid var(--extranet-accent);
}

.stats-label { color: #64748b; font-weight: 500; }
.stats-value { color: #0f172a; font-weight: 700; }

/* Table */
.modern-list-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
}

.modern-list-table thead th {
    padding: 16px 24px;
    background: #f8fafc;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid var(--extranet-accent);
    text-align: left;
}

.modern-list-table tbody td {
    padding: 16px 24px;
    vertical-align: middle;
    border-bottom: 1px solid var(--extranet-accent);
    color: #334155;
    font-size: 14px;
}

.modern-list-table tbody tr.clickable-row {
    cursor: pointer;
    transition: all 0.2s;
}

.modern-list-table tbody tr.clickable-row:hover {
    background: #f8faff;
}

.modern-list-table tbody tr.expanded {
    background: #f8f9ff;
}

.modern-list-table tbody tr.error-row {
    background: #fffcfc;
}

.col-no {
    color: #94a3b8 !important;
    font-weight: 500;
    text-align: center;
}

/* Pills */
.tenant-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid var(--extranet-accent);
}

.method-pill {
    display: inline-flex;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}
.method-pill.get { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
.method-pill.post { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
.method-pill.put { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
.method-pill.delete { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }

.type-pill {
    display: inline-flex;
    padding: 3px 8px;
    background: #f8fafc;
    color: #64748b;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    border: 1px solid var(--extranet-accent);
}
.type-pill.in { background: #e0f2fe; color: #0369a1; border-color: #bae6fd; }

.url-path-box {
    display: flex;
    flex-direction: column;
}

.url-text {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: #1e293b;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ip-address {
    font-size: 10px;
    color: #94a3b8;
}

.status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    border: 1px solid var(--extranet-accent);
    background: white;
}

.status-dot { width: 6px; height: 6px; border-radius: 50%; background: #cbd5e1; }

.status-pill.success { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
.status-pill.success .status-dot { background: #059669; }
.status-pill.warning { background: #fffbeb; color: #d97706; border-color: #fde68a; }
.status-pill.warning .status-dot { background: #d97706; }
.status-pill.danger { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
.status-pill.danger .status-dot { background: #dc2626; }

/* Action Button */
.action-btn {
    width: 36px;
    height: 36px;
    border-radius: var(--extranet-rounded);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--extranet-accent);
    background: white;
    color: #64748b;
    transition: all 0.2s;
    cursor: pointer;
}

.action-btn:hover, .action-btn.active {
    background: #1e293b;
    color: white;
    border-color: #1e293b;
}

/* Timestamp */
.timestamp-box { display: flex; flex-direction: column; }
.timestamp-box .date { font-weight: 600; color: #1e293b; }
.timestamp-box .time { font-size: 12px; color: #94a3b8; }

/* Detail Row */
.detail-container { padding: 0 !important; }
.detail-content {
    padding: 32px;
    background: #f8fafc;
    border-bottom: 2px solid var(--extranet-accent);
}

.state-card {
    background: white;
    border-radius: var(--extranet-rounded);
    border: 1px solid var(--extranet-accent);
    overflow: hidden;
    height: 100%;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.state-header {
    padding: 12px 20px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    background: var(--extranet-primary);
    color: white;
}

.state-body {
    padding: 0;
    max-height: 500px;
    overflow-y: auto;
    flex: 1;
}

.custom-json-pretty {
    background-color: var(--extranet-secondary) !important;
    padding: 16px !important;
}

:deep(.vjs-tree) {
    font-family: 'JetBrains Mono', monospace !important;
    font-size: 13px !important;
}

:deep(.vjs-key) { color: #38bdf8 !important; }
:deep(.vjs-value__string) { color: #4ade80 !important; }
:deep(.vjs-value__number) { color: #fbbf24 !important; }
:deep(.vjs-value__boolean) { color: #f472b6 !important; }
:deep(.vjs-colon), :deep(.vjs-brace), :deep(.vjs-bracket) { color: #94a3b8 !important; }

:deep(.vjs-ellipsis) {
    color: var(--extranet-primary) !important;
    font-weight: 800;
}

:deep(.vjs-carets) {
    color: #94a3b8 !important;
}

:deep(.vjs-carets svg) {
    color: #94a3b8 !important;
    fill: #94a3b8 !important;
}

:deep(.vjs-carets:hover) {
    color: var(--extranet-primary) !important;
}

:deep(.vjs-tree-brackets) {
    color: #94a3b8 !important;
}

:deep(.vjs-tree-brackets:hover) {
    color: var(--extranet-primary) !important;
}

:deep(.vjs-tree-node.dark:hover) {
    background-color: rgba(255,255,255,0.06) !important;
    border-radius: 4px !important;
}

/* Pagination Footer */
.card-footer {
    padding: 24px 32px;
    background: #ffffff;
    border-top: 1px solid #f1f5f9;
}

/* Loading/Empty State */
.loading-state, .empty-list { padding: 100px 32px; text-align: center; }
.empty-art { font-size: 48px; color: #e2e8f0; margin-bottom: 20px; }
.empty-list h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin-bottom: 8px; }
.empty-list p { color: #94a3b8; font-size: 14px; }

/* Responsive */
@media (max-width: 1200px) {
    .filter-bar { flex-direction: column; align-items: stretch; }
    .filter-left { flex-direction: column; align-items: stretch; }
}
</style>